import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert } from '@/design-system';
import type { Catalogue } from '@/lib/intake/catalogue';
import type { ApplicationView } from '@/lib/intake/api';
import { evaluate, todayIso } from '@/lib/intake/rules';
import { useDocuments } from '@/lib/intake/useDocuments';
import { useDraft } from '@/lib/intake/useDraft';
import { formatDate } from '@/lib/format';
import { ConfirmationStep } from './ConfirmationStep';
import { COPY } from './copy';
import { DocumentsStep } from './DocumentsStep';
import { LandingStep } from './LandingStep';
import { ReviewStep } from './ReviewStep';
import { SectionStep } from './SectionStep';
import { DOCUMENTS_STEP, REVIEW_STEP, StepNav } from './StepNav';
import { Toast } from './Toast';
import styles from './Journey.module.css';

export interface JourneyProps {
  catalogue: Catalogue;
  initial: ApplicationView;
  /** Open on the landing step. False straight after Start, which has already passed it. */
  startOnLanding: boolean;
  busy: boolean;
  onStartOver: () => void;
  onClear: () => void;
}

/**
 * The questionnaire: landing, eight sections, documents, review.
 *
 * The landing step is not a saved step. Signing in again shows it, with "Continue
 * where I left off" taking the applicant to the step they last opened, which is
 * the one the server holds (SPEC acceptance: another device, the same step).
 */
export function Journey({ catalogue, initial, startOnLanding, busy, onStartOver, onClear }: JourneyProps) {
  const labelOf = useCallback(
    (id: string) => catalogue.sections.flatMap((s) => s.fields).find((f) => f.id === id)?.label ?? id,
    [catalogue],
  );
  const draft = useDraft(initial, labelOf);
  const documents = useDocuments(initial.applicationId);
  const attached = documents.files ?? [];
  const [onLanding, setOnLanding] = useState(startOnLanding);
  // Once sent, the confirmation is where the applicant arrives; the answers are a
  // click away, read-only.
  const [viewingSent, setViewingSent] = useState(false);
  const sent = !draft.editable && draft.application.reference !== null;
  const confirming = sent && !viewingSent;
  const request = draft.application.status === 'INFORMATION_REQUESTED' ? draft.application.informationRequest : null;
  // Problems are shown once the person has left a field, or for answers that
  // arrived saved; not while the first characters of an address are typed.
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set(Object.keys(initial.answers)));
  const headingRef = useRef<HTMLHeadingElement>(null);
  const firstRender = useRef(true);

  const today = todayIso();
  const evaluation = useMemo(() => evaluate(catalogue, draft.answers, today), [catalogue, draft.answers, today]);
  const complete = useMemo(
    () => new Set(evaluation.sections.filter((s) => s.complete).map((s) => s.id)),
    [evaluation],
  );

  // A new step moves focus to its heading, so a screen reader announces where the
  // person now is and the next Tab starts at the top of the card.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    window.scrollTo({ top: 0 });
    headingRef.current?.focus();
  }, [draft.step, onLanding, confirming]);

  const go = (step: number) => {
    setOnLanding(false);
    draft.goTo(step);
  };
  const touch = (field: string) =>
    setTouched((before) => (before.has(field) ? before : new Set(before).add(field)));

  const sectionNumber = draft.step >= 1 && draft.step <= catalogue.sections.length ? draft.step : null;
  const section = sectionNumber === null ? null : catalogue.sections[sectionNumber - 1]!;

  return (
    <div className={styles.column}>
      {!draft.editable && !confirming && (
        <Alert tone="info">
          <span>{COPY.readOnly} </span>
          <button type="button" className={styles.gapLink} onClick={() => setViewingSent(false)}>
            {COPY.backToConfirmation}
          </button>
        </Alert>
      )}
      {request && (
        <Alert tone="info" title={COPY.infoRequestedTitle}>
          <p className={styles.request}>{request.reason}</p>
          {COPY.infoRequestedOn(formatDate(request.requestedAt))}
        </Alert>
      )}
      {confirming ? (
        <ConfirmationStep
          headingRef={headingRef}
          application={draft.application}
          catalogue={catalogue}
          evaluation={evaluation}
          onViewAnswers={() => {
            setViewingSent(true);
            go(REVIEW_STEP);
          }}
        />
      ) : onLanding ? (
        <LandingStep
          headingRef={headingRef}
          sections={catalogue.sections.length}
          consented={draft.application.consentedAt !== null}
          // Once past the landing step (consent is given there), there is somewhere to
          // continue to, even before a section is complete.
          anyComplete={complete.size > 0 || draft.application.consentedAt !== null}
          busy={busy}
          {...(draft.application.status === 'DRAFT' ? { onStartOver } : {})}
          onStart={() => {
            draft.giveConsent();
            go(1);
          }}
          onContinue={() => go(Math.max(1, draft.step))}
        />
      ) : (
        <>
          <StepNav
            sections={catalogue.sections}
            complete={complete}
            documentsComplete={attached.length > 0}
            current={draft.step}
            onGo={go}
          />
          {section && sectionNumber !== null && (
            <SectionStep
              key={section.id}
              headingRef={headingRef}
              catalogue={catalogue}
              section={section}
              number={sectionNumber}
              answers={draft.answers}
              evaluation={evaluation}
              touched={touched}
              editable={draft.editable}
              onAnswer={draft.setAnswer}
              onTouch={touch}
              onBack={() => (sectionNumber === 1 ? setOnLanding(true) : go(sectionNumber - 1))}
              onNext={() => go(sectionNumber + 1)}
            />
          )}
          {draft.step === DOCUMENTS_STEP && (
            <DocumentsStep
              headingRef={headingRef}
              catalogue={catalogue}
              highlighted={evaluation.highlightedDocuments}
              documents={documents}
              canAttach={draft.editable}
              canRemove={draft.application.status === 'DRAFT'}
              onBack={() => go(catalogue.sections.length)}
              onNext={() => go(REVIEW_STEP)}
            />
          )}
          {draft.step === REVIEW_STEP && (
            <ReviewStep
              headingRef={headingRef}
              applicationId={draft.application.applicationId}
              catalogue={catalogue}
              answers={draft.answers}
              evaluation={evaluation}
              files={attached}
              editable={draft.editable}
              submissionMode={draft.application.submissionMode}
              resending={draft.application.reference !== null}
              canClear={draft.application.status === 'DRAFT'}
              busy={busy}
              onEdit={go}
              onBack={() => go(DOCUMENTS_STEP)}
              onClear={onClear}
              onSubmit={draft.submit}
            />
          )}
        </>
      )}
      <Toast notice={draft.notice} />
    </div>
  );
}
